import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PositionGroupModel } from 'projects/workspace/src/app/shared/models';
import { PositionGroupModalComponent } from '../position-group-modal/position-group-modal.component';
import { CommonModalsActionsEnum } from '../common-modals-actions.enum';

type SubmitRequestFunction = (groupId: number|string) => Observable<PositionGroupModel[]>;
type AddGroupRequestFunction = (groupName: string) => Observable<PositionGroupModel[]>;
type GetListRequestFunction = () => Observable<PositionGroupModel[]>;

@Component({
  selector: 'rnpl-move-to-group-modal',
  templateUrl: './move-to-group-modal.component.html',
})
export class MoveToGroupModalComponent implements OnDestroy {

  public groupControl: FormControl =  new FormControl(null, [Validators.required]);
  public groupsList: PositionGroupModel[] = [];

  readonly destroy$: Subject<void> = new Subject();

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<MoveToGroupModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      submitRequest$: SubmitRequestFunction;
      getListRequest$: GetListRequestFunction;
      addGroupRequest$: AddGroupRequestFunction;
    }
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.getGroups();
  }

  public getGroups(setAddedGroup = false): void {
    this.data.getListRequest$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((groups: PositionGroupModel[]) => {
        if (this.groupsList && this.groupsList.length && setAddedGroup) {
          const addedGroups = groups.filter(item2 => !this.groupsList.some(item1 => item1.groupId === item2.groupId));
          if (addedGroups && addedGroups.length) {
            this.groupControl.patchValue(addedGroups[0].groupId);
          }
        }
        this.groupsList = [...groups]
          .filter(g => !g.system || g.isDefault)
          .sort((a, b) => Number(a.isDefault) - Number(b.isDefault));
      });
  }

  public createGroup(): void {
    const dialog = this.dialog.open(PositionGroupModalComponent, {
      data: {
        submitRequest$: this.data.addGroupRequest$
      },
    });

    dialog.afterClosed().subscribe((res) => {
      if (res === CommonModalsActionsEnum.CONFIRM) {
        this.getGroups(true);
      }
    });
  }

  public submit(): void {
    this.groupControl.markAsTouched();
    this.groupControl.updateValueAndValidity();
    if (this.groupControl.invalid) { return; }

    this.data.submitRequest$(this.groupControl.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.dialogRef.close();
      }); // updated via store
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
